/**
 * my360 plans — single source of truth for the entire monorepo.
 *
 * Anything plan-related (limits, prices, feature gating, UI labels)
 * MUST consume this module. Do NOT duplicate plan slugs/limits/prices
 * elsewhere — DB seed, Asaas service, frontend pricing page all import
 * from here.
 *
 * To change a price or add a feature: edit ONLY this file. Then run the
 * `bun run apps/api/src/db/sync-plans.ts` script to push changes into the
 * `plans` table for tenants currently subscribed.
 */

/**
 * Quanto de IA cabe em cada plano por mês.
 *
 * Um crédito é a unidade de custo, não de ação: ler uma cena custa 1, gerar
 * uma cena 4K custa 50, porque uma imagem do Gemini custa cerca de cinquenta
 * vezes uma leitura.
 *
 * Os números são proporcionais ao que o plano paga, e não iguais para todos.
 * Antes eram: o Enterprise pagava dez vezes mais que o Pro e recebia a mesma
 * cota, o que é ruim dos dois lados — mesquinho para quem paga caro e sem
 * defesa para nós, já que a conta grande é justamente a que usa mais.
 *
 * O teto de custo que cada um representa, ao nosso preço de custo MEDIDO em
 * produção (US$ 0,00152 por leitura, US$ 0,2678 por geração), fica no
 * comentário de cada plano.
 */
const AI_CREDITS = {
  /** Pro: até US$ 2,14/mês de IA sobre R$ 89,90 — se gastar TUDO gerando. */
  pro: 400,
  /** Enterprise: até US$ 10,71/mês sobre R$ 899,90 — proporcionalmente MENOR. */
  enterprise: 2000,
  /** Testadores: o suficiente para experimentar tudo sem virar produção. */
  beta: 120,
} as const;

export type PlanSlug = 'free' | 'pro' | 'enterprise' | 'beta';

export interface PlanLimits {
  /** -1 = unlimited */
  maxProjects: number;
  /** -1 = unlimited */
  maxScenes: number;
  /** Bytes. -1 = unlimited */
  maxStorage: number;
  /** Allow removing my360 watermark / "Powered by" badge */
  whiteLabel: boolean;
  /** WebXR / VR mode */
  vr: boolean;
  /** Analytics dashboard access */
  analytics: boolean;
  /** Custom domain (CNAME) */
  customDomain: boolean;
  /** Password-protected tours */
  passwordProtect: boolean;
  /** Lead capture forms */
  leadCapture: boolean;
  /** Decorar uma cena com IA e mapear os pontos automaticamente */
  aiStaging: boolean;
  /**
   * Créditos de IA por mês. Cada ação custa o que ela nos custa:
   * ler uma cena e propor pontos = 1; gerar uma cena decorada em 4K = 50,
   * porque uma imagem do Gemini custa cerca de cinquenta vezes uma leitura.
   * -1 = sem limite. 0 = o plano não tem IA.
   */
  aiCreditsMonthly: number;
  /** Team members / multi-seat */
  teamSeats: number;
}

export interface PlanDefinition {
  slug: PlanSlug;
  name: string;
  /** True real price charged in BRL (cents). 0 = free. */
  priceCents: number;
  /** Display "from" price (struck through). null = no discount illusion. */
  displayFromCents: number | null;
  /** Marketing tagline shown under the plan name */
  tagline: string;
  /**
   * Bullet list shown on pricing card. Mark as `included: false` to render greyed-out.
   *
   * INVARIANT: every `label` must name a BENEFIT (something the customer gets),
   * never a limitation — `included` alone carries the yes/no. Writing a
   * limitation as a label ("Marca my360 visível") inverts its meaning when
   * rendered with `included: false`. Labels must also agree with `limits`.
   */
  features: { label: string; included: boolean; emphasize?: boolean }[];
  /** Plan-level limits enforced server-side */
  limits: PlanLimits;
  /** If true, plan is hidden from public /pricing — only assignable internally */
  hidden: boolean;
  /** Plan tier for ordering/comparison (free=0, pro=1, enterprise=2, beta=99) */
  tier: number;
}

/**
 * Quantos créditos custa cada ação, para quem precisa TRADUZIR cota em
 * unidades que a pessoa entende ("8 cenas geradas", não "400 créditos").
 *
 * Espelha AI_COST na API. Vive aqui porque a landing e a página de planos
 * precisam da conta e não podem importar código de servidor — e porque a
 * alternativa era escrever "20 gerações" à mão numa string de tradução, que
 * foi exatamente como a landing passou a prometer duas vezes e meia o que o
 * plano entrega.
 */
export const AI_COST_CREDITS = { annotate: 1, enhance: 1, chat: 1, compact: 1, plan: 5, generate: 50 } as const;

const GB = 1024 * 1024 * 1024;

/**
 * As cotas são apertadas de propósito.
 *
 * Foram 2 / 25 / 50 / 200 GB e quase ninguém chegava perto: a maior conta
 * da plataforma usava 0,19 GB de 2. Cota que nunca acaba não é generosidade,
 * é um pacote de gigas que nunca vende — e foram DUAS unidades em quatro
 * meses.
 *
 * A referência agora é vídeo, não foto. Uma cena de vídeo 360 normalizada
 * ocupa cerca de 236 MB, então 1 GB são quatro cenas e 10 GB são quarenta.
 * Em foto continua folgado (uma equirect comprimida não passa de poucos
 * megas), que é o uso de quem está experimentando.
 *
 * O Enterprise fica onde estava: ele é o plano que se vende dizendo "não
 * pense em espaço", e apertá-lo tiraria a única coisa que o diferencia de
 * Pro + pacotes.
 */

export const PLANS: Record<PlanSlug, PlanDefinition> = {
  free: {
    slug: 'free',
    name: 'Free',
    priceCents: 0,
    displayFromCents: null,
    tagline: 'Comece grátis e publique seu primeiro tour',
    tier: 0,
    hidden: false,
    features: [
      { label: 'Tours 360° ilimitados', included: true, emphasize: true },
      { label: '1 GB de storage', included: true, emphasize: true },
      { label: 'Hotspots de informação, link, vídeo', included: true },
      { label: 'VR / WebXR', included: true },
      // Every row names a BENEFIT; `included` says whether the plan grants it.
      // (Was "Marca my360 visível: não" — literally backwards, since Free is
      // exactly the plan where the badge IS shown.)
      { label: 'Sem marca my360', included: false },
      { label: 'Domínio próprio', included: false },
      { label: 'Analytics avançado', included: false },
      { label: 'Captura de leads', included: false },
      { label: 'Decoração com IA', included: false },
    ],
    limits: {
      maxProjects: -1,
      maxScenes: -1,
      maxStorage: 1 * GB,
      whiteLabel: false,
      vr: true,
      analytics: false,
      customDomain: false,
      passwordProtect: false,
      leadCapture: false,
      aiStaging: false,
      aiCreditsMonthly: 0,
      teamSeats: 1,
    },
  },

  pro: {
    slug: 'pro',
    name: 'Pro',
    priceCents: 8990, // R$ 89,90
    displayFromCents: 14900, // ~40% off illusion
    tagline: 'Para profissionais que vendem com tours imersivos',
    tier: 1,
    hidden: false,
    features: [
      { label: 'Tudo do Free', included: true },
      { label: '10 GB de storage', included: true, emphasize: true },
      { label: 'Sem marca my360 (white-label)', included: true, emphasize: true },
      { label: 'Domínio próprio', included: true, emphasize: true },
      { label: 'Analytics completo', included: true },
      { label: 'Tours protegidos por senha', included: true },
      { label: 'Captura de leads', included: true },
      { label: 'Decoração com IA', included: true },
      { label: 'Suporte prioritário', included: true },
    ],
    limits: {
      maxProjects: -1,
      maxScenes: -1,
      maxStorage: 10 * GB,
      whiteLabel: true,
      vr: true,
      analytics: true,
      customDomain: true,
      passwordProtect: true,
      leadCapture: true,
      aiStaging: true,
      aiCreditsMonthly: AI_CREDITS.pro,
      teamSeats: 1,
    },
  },

  enterprise: {
    slug: 'enterprise',
    name: 'Enterprise',
    priceCents: 89990, // R$ 899,90
    displayFromCents: 149900, // ~40% off illusion
    tagline: 'Para empresas e agências com volume',
    tier: 2,
    hidden: false,
    // NOTE: only sell what exists — teams/API/SLA were removed from the
    // display bullets because they have no implementation yet.
    features: [
      { label: 'Tudo do Pro', included: true },
      { label: '200 GB de storage', included: true, emphasize: true },
      { label: 'Onboarding dedicado', included: true },
      { label: 'Suporte 24/7', included: true },
    ],
    limits: {
      maxProjects: -1,
      maxScenes: -1,
      maxStorage: 200 * GB,
      whiteLabel: true,
      vr: true,
      analytics: true,
      customDomain: true,
      passwordProtect: true,
      leadCapture: true,
      aiStaging: true,
      aiCreditsMonthly: AI_CREDITS.enterprise,
      teamSeats: 10,
    },
  },

  beta: {
    slug: 'beta',
    name: 'Beta',
    priceCents: 500, // R$ 5 — oferta promocional inicial (será removida)
    displayFromCents: null,
    tagline: 'Acesso completo a TUDO por R$5/mês — oferta limitada',
    tier: 99,
    // hidden=true → fora da /pricing pública (marketing pra prospect),
    // mas /billing/plans (logado) busca direto do DB sem filtro de
    // hidden, então picker mostra mesmo assim. Quando a oferta acabar,
    // basta deletar da DB ou subir hidden em código.
    hidden: true,
    features: [
      // NÃO dizer "mesmo do Enterprise": o Beta tem 50 GB contra os 200 GB do
      // Enterprise, então a igualdade era falsa (e contradizia a linha logo
      // abaixo). O que de fato é igual são os RECURSOS, não a cota.
      { label: 'Todos os recursos premium desbloqueados', included: true, emphasize: true },
      { label: '10 GB de storage', included: true },
      { label: 'Domínio próprio + white-label', included: true },
      { label: 'Analytics + leads', included: true },
      { label: 'Oferta limitada — vai sair em breve', included: true },
    ],
    limits: {
      maxProjects: -1,
      maxScenes: -1,
      maxStorage: 10 * GB,
      whiteLabel: true,
      vr: true,
      analytics: true,
      customDomain: true,
      passwordProtect: true,
      leadCapture: true,
      aiStaging: true,
      aiCreditsMonthly: AI_CREDITS.beta,
      teamSeats: -1,
    },
  },
};

/** Plans visible on /pricing in display order */
export const PUBLIC_PLANS: PlanDefinition[] = (Object.values(PLANS) as PlanDefinition[])
  .filter((p) => !p.hidden)
  .sort((a, b) => a.tier - b.tier);

/** All plans including hidden ones (for admin/internal use) */
export const ALL_PLANS: PlanDefinition[] = (Object.values(PLANS) as PlanDefinition[]).sort(
  (a, b) => a.tier - b.tier,
);

/** Storage packs sellable to ANY plan (including Free) */
export interface StoragePack {
  slug: string;
  name: string;
  /** Bytes added to plan's maxStorage when active */
  bonusBytes: number;
  priceCents: number;
}

/**
 * Preços pensados para caber no impulso, não para maximizar a margem.
 *
 * Vinham de R$ 19 / R$ 79 / R$ 249, com margem de 94-95%. Em quatro meses
 * venderam DUAS unidades — ou seja, não havia receita a proteger, e a
 * margem alta estava defendendo um número que ninguém pagava.
 *
 * Agora somam abaixo dos limiares que a pessoa decide sem pensar: menos de
 * R$ 15, menos de R$ 50, menos de R$ 100. O giga sai a R$ 1,50 / R$ 1,00 /
 * R$ 0,50 — uma escada que recompensa subir. Mesmo no pior caso (pacote
 * inteiro ocupado o mês todo) a margem não desce de 89%, porque o giga nos
 * custa R$ 0,037.
 */
export const STORAGE_PACKS: StoragePack[] = [
  { slug: 'pack-10gb', name: '+10 GB', bonusBytes: 10 * GB, priceCents: 1499 },
  { slug: 'pack-50gb', name: '+50 GB', bonusBytes: 50 * GB, priceCents: 4999 },
  { slug: 'pack-200gb', name: '+200 GB', bonusBytes: 200 * GB, priceCents: 9999 },
];

// ─── Helpers ──────────────────────────────────────────────────────────

/** Get plan definition with safe fallback to free */
export function getPlan(slug: string | null | undefined): PlanDefinition {
  if (!slug) return PLANS.free;
  return PLANS[slug as PlanSlug] ?? PLANS.free;
}

export function getPlanLimits(slug: string | null | undefined): PlanLimits {
  return getPlan(slug).limits;
}

/** Returns true if `slug` plan unlocks the named feature */
export function planHasFeature(
  slug: string | null | undefined,
  feature: keyof Pick<
    PlanLimits,
    'whiteLabel' | 'vr' | 'analytics' | 'customDomain' | 'passwordProtect' | 'leadCapture' | 'aiStaging'
  >,
): boolean {
  return getPlan(slug).limits[feature] === true;
}

/** "R$ 89,00" — display formatter */
export function formatPriceBRL(cents: number): string {
  if (cents === 0) return 'R$ 0';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

/** "2 GB" / "200 GB" / "Ilimitado" */
export function formatStorage(bytes: number): string {
  if (bytes === -1) return 'Ilimitado';
  const gb = bytes / GB;
  if (gb >= 1) return `${gb % 1 === 0 ? gb : gb.toFixed(1)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.round(mb)} MB`;
}

/** "40%" off label, computed from priceCents vs displayFromCents */
export function computeDiscountPercent(plan: PlanDefinition): number | null {
  if (!plan.displayFromCents || plan.priceCents === 0) return null;
  const pct = Math.round((1 - plan.priceCents / plan.displayFromCents) * 100);
  return pct > 0 ? pct : null;
}

/** Cheapest plan that unlocks a given feature (for "Disponível no Pro" tooltips) */
export function minimumPlanFor(
  feature: keyof Pick<
    PlanLimits,
    'whiteLabel' | 'vr' | 'analytics' | 'customDomain' | 'passwordProtect' | 'leadCapture'
  >,
): PlanDefinition | null {
  for (const plan of PUBLIC_PLANS) {
    if (plan.limits[feature]) return plan;
  }
  return null;
}
